import { Component, HostListener, OnInit, OnDestroy, ViewChild, ViewContainerRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CtrlBaseComponent } from '../ctrl-base.component';
import { Color } from '../../enums/color.enum';
import { ButtonType } from '../../enums/button.enum';
import { CtButtonCompressedContainerComponent } from '../ct-button-compressed-container/ct-button-compressed-container.component';
import { ADN_PLUS, Action } from '../../models';
import { breakpoints } from '../../environments/environment.breakpoints';

@Component({
  selector: 'ct-button',
  templateUrl: './ct-button.component.html',
  styleUrls: ['./ct-button.component.css'],
  host: {
    '[hidden]': 'getHidden()'
  }
})
export class CtButtonComponent extends CtrlBaseComponent implements OnInit, OnDestroy, AfterViewInit{

  //util
  private throttleTime: number = 2000;
  public clickTimer = new Subject();
  private clickSub: Subscription;
  private eventRef: string;
  public ButtonColor = Color;
  public TagColor = Color;
  public ButtonType = ButtonType;
  public isHovered : boolean = false;
  private value : ADN_PLUS; 
  
  //properties
  private buttonText: string;
  private buttonColor : Color;
  private type : ButtonType = ButtonType.Button;
  public  description: string;
  private disabled: boolean;
  private tooltip : string;
  private buttonContainerParentId : string; //this property is automatically set by the button container component when it paints this component
  private tag : string;
  private descriptionValue: string;
  private tagColor: Color;

  //style
  private btnBlock: boolean = true;
  private width : number;
  private icon: string;

  public isMediumSize : boolean = false;
  public isSmallSize : boolean = false;
  public isExtraSmall : boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.manageWindowResizing(window.innerWidth);
  }

  private manageWindowResizing(width: number) {
    this.isSmallSize = width < breakpoints.L && width >= breakpoints.M;
    this.isExtraSmall = width >= breakpoints.S && width < breakpoints.M;
    this.isMediumSize = width >= breakpoints.L;
  }

  constructor(private cdr : ChangeDetectorRef) {
    super();
  }

  
  ngOnInit() {
    this.onResize();
    this.manageButtonColor();
    super.ngOnInit();
    this.clickSub = this.clickTimer.pipe(
      throttleTime(this.throttleTime)
      ).subscribe((evt: Event) => this.onClick(evt));
  }
    
  ngAfterViewInit(): void {
    if(this.buttonContainerParentId) this.removeDuplicatedConditionsParams();
  }

  /**
   * when buttons are created inside the buttonContainer component condition params are duplicated cause an extrange behaviour
   * this method removes the duplicated condition params
   */
  removeDuplicatedConditionsParams() {
    let set : Set<string>;
    this.getControlActions().forEach((action : Action) => {
      if(action.eventName = 'onClick'){
        set = new Set<string>([...action.conditionParams]);
        action.conditionParams = new Array(...set);
      }
    });
  }

  private manageButtonColor() {
    let type : string = this.getMetaData().controlProperties.buttonColor;
    if(type == null){
      this.buttonColor = Color.Secondary;
      return;
    }
    let metadata : any = this.getMetaData();
    const firstLetter = type.charAt(0).toUpperCase();
    const restOfString = type.slice(1);
    firstLetter + restOfString;
    metadata.controlProperties.buttonColor = firstLetter + restOfString;
    this.setMetaData(metadata);
  }

  ngOnDestroy() {
    if(this.clickSub){
      this.clickSub.unsubscribe();
    }
  }

  @HostListener('click', ['$event'])
  validateClickEvent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.clickTimer.next(event);
  }

  onClick(evt: Event): void {
    if(this.buttonContainerParentId){
      this.cdr.detectChanges();
      const buttonContainerParent = this.utilService.getComponentRef(this.buttonContainerParentId) as CtButtonCompressedContainerComponent;
      buttonContainerParent.onButtonClick();
    }
    if (!this.getDisabled())
      this.event2('onClick');
    
    if(this.buttonContainerParentId) evt.stopPropagation();
  }

  public getButtonWidth(): string { 
    if(!this.getBtnBlock() && this.getWidth() === null) return '100%';
    if(this.getBtnBlock()) {
        return '100%';
    } else if(this.getWidth() !== null) {
        return this.getWidth();
    } else{
        return '100%';
    }
  }

  public hasIcon(): boolean { 
    return Boolean(this.getIcon()); 
  }

  /**
   * The function returns true if the button is hovered, disabled, or has a secondary or danger color.
   * @returns The method is returning a boolean value.
   */
  public showLine(): boolean {
    return this.isHovered 
      || this.getDisabled() 
      || this.getButtonColor() === this.ButtonColor.Secondary 
      || this.getButtonColor() === this.ButtonColor.Danger;
  }
  
  /**
   * The function returns the CSS class name for a button based on its color and disabled state.
   * @returns The method `getButtonClass()` returns a string representing the CSS class name for a button
   * based on its disabled state and color.
   */
  public getButtonClass() {
    if (this.getDisabled()) {
      return this.getButtonColor() === this.ButtonColor.Default ? 'btn-default-disabled' : 'btn-disabled';
    } 
    switch(this.getButtonColor()) {
      case this.ButtonColor.Primary: return 'btn-primary';
      case this.ButtonColor.Secondary: return 'btn-secondary';
      case this.ButtonColor.Default: return 'btn-default';
      case this.ButtonColor.Danger: return 'btn-danger';
      case this.ButtonColor.DefaultButtonContainer: return 'btn-default-container';
      case this.ButtonColor.PrimaryButtonContainer: return 'btn-primary-container';
      case this.ButtonColor.DangerButtonContainer: return 'btn-danger-container';
      default: return '';
    }
  }

  /**
   * The function returns a string representing the CSS class for a button with an icon based on its
   * color and disabled state.
   * @returns The method is returning a string that represents the class(es) to be applied to a button
   * element.
   */
  public getButtonWithIconClass(): string {
    let btnClass = '';

    if (this.getButtonColor() === this.ButtonColor.Primary 
      || this.getButtonColor() === this.ButtonColor.Secondary 
      || this.getButtonColor() === this.ButtonColor.Danger
      || this.getButtonColor() === this.ButtonColor.PrimaryButtonContainer) {
      btnClass += ' th-text-white';
    }
    
    if (this.getButtonColor() === this.ButtonColor.Default 
      || this.getButtonColor() === this.ButtonColor.DefaultButtonContainer
      || this.getButtonColor() === this.ButtonColor.DangerButtonContainer) {
      btnClass += ' th-text-main';
    }
    
    if (this.getButtonColor() === this.ButtonColor.Primary && !this.getDisabled()) {
      btnClass += ' btn-primary';
    }

    if(this.getButtonColor() === this.ButtonColor.PrimaryButtonContainer) {
      btnClass += ' btn-primary-container';
    }

    if(this.getButtonColor() === this.ButtonColor.DangerButtonContainer) {
      btnClass += ' btn-danger-container';
    }

    if(this.getButtonColor() === this.ButtonColor.DefaultButtonContainer) {
      btnClass += ' btn-default-container';
    }
    
    if (this.getButtonColor() === this.ButtonColor.Secondary && !this.getDisabled() 
      || (this.getButtonColor() === this.ButtonColor.Primary && this.isHovered) && !this.getDisabled()) {
      btnClass += ' btn-secondary';
    }
    
    if (this.getButtonColor() === this.ButtonColor.Danger && !this.getDisabled()) {
      btnClass += ' btn-danger';
    }
    
    if (this.getDisabled()) {
      btnClass += ' btn-disabled';
    }

    return btnClass.trim();
  }

  /**
   * The function `getIconClass` returns a string representing the CSS classes for an icon based on the
   * button color and disabled state.
   * @returns The method is returning a string that represents the CSS class(es) for the icon element.
   */
  public getIconClass(): string {
    let iconClass = '';


    if(this.getButtonColor() === this.ButtonColor.PrimaryButtonContainer) {
      iconClass += ' th-bg-main btn-primary-container-line';
    }

    if (this.getButtonColor() === this.ButtonColor.Primary || this.getButtonColor() === this.ButtonColor.Secondary) {
      iconClass += ' th-bg-secondary';
    }
    
    if (this.getButtonColor() === this.ButtonColor.Default || this.getButtonColor() === this.ButtonColor.DefaultButtonContainer
    || this.getButtonColor() === this.ButtonColor.DangerButtonContainer) {
      iconClass += ' th-bg-white';
    }
    
    if (this.getButtonColor() === this.ButtonColor.Danger) {
      iconClass += ' th-bg-alert';
    }
    
    if (this.getDisabled()) {
      iconClass += ' btn-disabled';
    }

    if(this.getButtonColor() === this.ButtonColor.DefaultButtonContainer || this.getButtonColor() === this.ButtonColor.DangerButtonContainer) {
      iconClass += ' th-line btn-container-line-remove';
    }

    return iconClass.trim();
  }

  public getIconColor(): string {
    let iconClass = '';
    switch(this.getButtonColor()) {
      case this.ButtonColor.DefaultButtonContainer: iconClass += ' th-text-main'; break;
      case this.ButtonColor.PrimaryButtonContainer: iconClass += ' th-text-white'; break;
      case this.ButtonColor.DangerButtonContainer: iconClass += ' th-text-alert'; break;
      default: iconClass += ' th-text-white'; break;
    }
    return iconClass.trim();
  }

  public getButtonIconColor(): string {
    let classes : string = '';
    switch(this.getButtonColor()) {
      case this.ButtonColor.Primary: classes = 'th-text-main'; break;
      case this.ButtonColor.Secondary: classes = 'th-text-secondary'; break;
      case this.ButtonColor.Danger: classes = 'th-text-alert'; break;
      case this.ButtonColor.Default: classes = 'th-text-main'; break;
      default: classes = 'th-text-main'; break;
    }
    return classes + ' fa-2x';
  }

  public setButtonColorManually(color: string): void {
    const colorEnum : Color = Color[color as keyof typeof Color];
    if(colorEnum) {
      this.setButtonColor(colorEnum);
    }
  }

  public onHover(isHovered : boolean): void {
    this.isHovered = isHovered;
  }

  public getEffectiveClass(): string {
    if (this.getStyleClass()) {
        return this.getStyleClass();
    }
    if (this.isMediumSize) {
        return this.getMdStyleClass();
    }
    if (this.isSmallSize) {
        return this.getSmStyleClass();
    }
    if (this.isExtraSmall) {
        return this.getXsStyleClass();
    }
    return ''; // default class or empty if none
  } 

  //GETTERS
  getDisabled(): boolean { return this.disabled }
  getButtonText(): string { return this.buttonText }
  getWidth(): string { return this.width+'px' }
  getDescription(): string { return this.description }
  getEventRef(): string { return this.eventRef }
  getBtnBlock(): boolean { return this.btnBlock }
  getIcon(): string { return this.icon }
  getType(): ButtonType { return this.type }
  getButtonColor(): Color { return this.buttonColor }
  getTooltip(): string { return this.tooltip }
  getButtonContainerParentId(): string { return this.buttonContainerParentId }
  getTag(): string { return this.tag }
  getDescriptionValue(): string {return this.descriptionValue}
  getValue(): ADN_PLUS {return this.value}
  getTagColor(): Color {return this.tagColor}


  //SETTERS
  setDisabled(disabled: boolean): void { this.disabled = disabled }
  setButtonText(buttonText: string): void { this.buttonText = buttonText }
  setIcon(icon: string): void { this.icon = icon }
  setBtnBlock(btnBlock: boolean): void { this.btnBlock = btnBlock }
  setEventRef(eventRef: string): void { this.eventRef = eventRef }
  setDescription(description: string): void { this.description = description }
  setWidth(width: number): void { this.width = width }
  setType(type: ButtonType): void { this.type = type }
  setButtonColor(buttonColor: Color): void { this.buttonColor = buttonColor }
  setTooltip(tooltip: string): void { this.tooltip = tooltip }
  setButtonContainerParentId(buttonContainerParentId: string): void { this.buttonContainerParentId = buttonContainerParentId }
  setTag(tag: string) : void {this.tag = tag}
  setDescriptionValue( descriptionValue: string) : void { this.descriptionValue = descriptionValue}
  setValue(value: ADN_PLUS): void { this.value = value; }
  setTagColor(tagColor: Color): void { this.tagColor = tagColor}
}
